import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Navigation
    nav: {
      tivorao: 'Tivorao',
      scribai: 'ScribAI',
      buy: 'Buy',
    },
    // Homepage
    home: {
      introducing: 'Introducing',
      brandName: 'Tivorao',
      tagline: 'Redefining how you capture life.',
      brandDescription: 'At Tivorao, we believe that the best ideas happen in conversation. Our mission is to create intelligent tools that help you capture, understand, and act on the moments that matter most.',
      discoverScribai: 'Discover ScribAI',
      ourVision: 'Our Vision',
      visionTitle: 'Technology that listens, so you can focus.',
      visionText: 'We are building a future where technology seamlessly captures your conversations, transforming spoken words into structured knowledge. No more forgotten ideas. No more missed details.',
      introducingScribai: 'Introducing ScribAI',
      scribaiSubtitle: 'Your AI-powered voice companion',
      scribaiDescription: 'ScribAI is our flagship product—a slim, elegant AI voice recorder that transforms every conversation into actionable intelligence. Record, transcribe, summarize, and organize with the power of AI.',
      learnMore: 'Learn more',
      buyNow: 'Buy now',
      problemTitle: 'The problem with memory.',
      problemText: 'Within 24 hours, we forget up to 70% of what we hear. Important ideas, action items, and insights slip away.',
      forgotten: 'Forgotten within 24 hours',
      incomplete: 'Notes incomplete from manual writing',
      lostWeekly: 'Lost weekly reconstructing info',
      heroWord1: 'OWN',
      heroWord2: 'YOUR',
      heroWord3: 'CONVERSATIONS',
      heroTag1: 'AI Transcription',
      heroTag2: '30HR Battery',
      heroFootnote: 'Every conversation holds an idea worth keeping. ScribAI listens, so your mind can wander freely.',
      heroRecording: 'Recording',
      visionImageAlt: 'ScribAI recorder paired with the companion app',
      introImageAlt: 'ScribAI recorder, detail view',
      loveItBannerAlt: 'Why you\'ll love ScribAI — AI transcription, 30-hour battery, ultra-slim design',
    },
    smartSummaries: {
      headline1: 'Smart Summaries',
      headline2: '& Mind Maps',
      subhead: 'Every recording is automatically organized into clear, structured summaries you can actually use.',
      phoneHeader: 'All Recordings',
      summarized: 'Summarized',
      useCase1: 'Call Recording',
      useCase2: 'Meeting Minutes',
      useCase3: 'Lecture Transcripts',
      useCase4: 'Interview Summaries',
      useCase5: 'Mind Maps',
      useCase6: 'Action Items',
    },
    // Experience page
    experience: {
      title: 'Intelligence from voice.',
      subtitle: 'ScribAI goes beyond recording. It understands, organizes, and delivers actionable insights.',
      howItWorks: 'How it works',
      workflowSubtitle: 'Three simple steps to transform your productivity.',
      step1Title: 'Record',
      step1Desc: 'Press once to start. ScribAI captures up to 30 hours of crystal-clear audio.',
      step2Title: 'Sync',
      step2Desc: 'Connect to the app. Your recordings are instantly uploaded and processed by AI.',
      step3Title: 'Access',
      step3Desc: 'Get your transcripts, summaries, and action items. Everything organized and searchable.',
      featuresTitle: 'Powerful features.',
      featuresSubtitle: 'Everything you need to capture, understand, and act on your conversations.',
      feature1: 'AI Transcription',
      feature1Desc: 'Crystal-clear voice-to-text with speaker identification. Supports 104 languages with >98% accuracy.',
      feature2: 'Smart Summaries',
      feature2Desc: 'Automatic condensation of hours into digestible key points using GPT-4.0.',
      feature3: 'Insight Extraction',
      feature3Desc: 'AI identifies important concepts, decisions, and action items.',
      feature4: 'Task Generation',
      feature4Desc: 'Automatically creates action items from spoken commitments.',
      feature5: 'Smart Search',
      feature5Desc: 'Find any moment across all your recordings instantly.',
      feature6: 'Auto Organization',
      feature6Desc: 'Notes categorized and tagged by topic automatically.',
      useCasesTitle: 'Built for everyone.',
      useCasesSubtitle: 'ScribAI adapts to your world.',
      forStudents: 'For Students',
      studentsDesc: 'Record lectures. Get study notes. Ace your exams.',
      forProfessionals: 'For Professionals',
      professionalsDesc: 'Own every meeting. Never miss an action item.',
      forCreators: 'For Creators',
      creatorsDesc: 'Capture ideas. Organize thoughts. Create more.',
      specsTitle: 'Designed for real life.',
      recording: 'Recording',
      weight: 'Weight',
      audioQuality: 'Audio Quality',
      thin: 'Thin',
      ctaTitle: 'Ready to transform how you capture ideas?',
      ctaSubtitle: 'Choose your color and get started.',
    },
    // Product page
    product: {
      chooseColor: 'Choose your color.',
      from: 'From',
      color: 'Color',
      buyButton: 'Buy',
      inTheBox: "What's in the box.",
      recorder: 'ScribAI Recorder',
      cable: 'USB-C Charging Cable',
      case: 'Premium Carrying Case',
      guide: 'Quick Start Guide',
      subscription: '1-Year ScribAI Premium',
      warranty: 'Warranty Card',
      specsTitle: 'Tech Specs.',
      model: 'Model',
      audioFormat: 'Audio Format',
      sampling: 'Sampling',
      storage: 'Storage',
      battery: 'Battery',
      weightSpec: 'Weight',
      dimensions: 'Dimensions',
      freeShipping: 'Free Shipping',
      freeShippingDesc: 'Worldwide delivery at no extra cost.',
      returns: '30-Day Returns',
      returnsDesc: 'Not satisfied? Full refund, no questions.',
      warrantyInfo: '2-Year Warranty',
      warrantyDesc: "We've got you covered.",
      finalCta: 'ScribAI.',
    },
    // Footer
    footer: {
      brand: 'Tivorao',
      tagline: 'Redefining how you capture life.',
      product: 'Product',
      aboutTivorao: 'About Tivorao',
      scribaiExperience: 'ScribAI Experience',
      getScribai: 'Get ScribAI',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      shipping: 'Shipping',
      returns: 'Returns & Refunds',
      contact: 'Contact',
      legalNotice: 'Legal Notice',
      securePayments: 'Secure Payments',
      copyright: '© 2026 Tivorao. All rights reserved.',
      newsletterTitle: 'Stay in the loop',
      newsletterDesc: 'Product updates and early access. No spam.',
      newsletterPlaceholder: 'Your email',
      newsletterButton: 'Subscribe',
      newsletterSuccess: "You're on the list.",
      newsletterInvalid: 'Please enter a valid email.',
    },
    // Legal pages
    legal: {
      privacyTitle: 'Privacy Policy',
      termsTitle: 'Terms of Service',
      shippingTitle: 'Shipping Policy',
      returnsTitle: 'Returns & Refund Policy',
      contactTitle: 'Contact Information',
      legalNoticeTitle: 'Legal Notice',
      lastUpdated: 'Last updated',
    },
  },
  es: {
    // Navigation
    nav: {
      tivorao: 'Tivorao',
      scribai: 'ScribAI',
      buy: 'Comprar',
    },
    // Homepage
    home: {
      introducing: 'Presentamos',
      brandName: 'Tivorao',
      tagline: 'Redefiniendo cómo capturas la vida.',
      brandDescription: 'En Tivorao, creemos que las mejores ideas surgen en las conversaciones. Nuestra misión es crear herramientas inteligentes que te ayuden a capturar, comprender y actuar en los momentos que más importan.',
      discoverScribai: 'Descubre ScribAI',
      ourVision: 'Nuestra Visión',
      visionTitle: 'Tecnología que escucha, para que puedas concentrarte.',
      visionText: 'Estamos construyendo un futuro donde la tecnología captura tus conversaciones de manera fluida, transformando las palabras habladas en conocimiento estructurado. Sin más ideas olvidadas. Sin más detalles perdidos.',
      introducingScribai: 'Presentamos ScribAI',
      scribaiSubtitle: 'Tu compañero de voz impulsado por IA',
      scribaiDescription: 'ScribAI es nuestro producto estrella—una grabadora de voz con IA elegante y delgada que transforma cada conversación en inteligencia accionable. Graba, transcribe, resume y organiza con el poder de la IA.',
      learnMore: 'Más información',
      buyNow: 'Comprar ahora',
      problemTitle: 'El problema con la memoria.',
      problemText: 'En 24 horas, olvidamos hasta el 70% de lo que escuchamos. Ideas importantes, tareas pendientes e información valiosa se pierden.',
      forgotten: 'Olvidado en 24 horas',
      incomplete: 'Notas incompletas al escribir manualmente',
      lostWeekly: 'Perdido semanalmente reconstruyendo información',
      heroWord1: 'DOMINA',
      heroWord2: 'TUS',
      heroWord3: 'CONVERSACIONES',
      heroTag1: 'Transcripción IA',
      heroTag2: '30H de Batería',
      heroFootnote: 'Cada conversación guarda una idea que merece quedarse. ScribAI escucha, para que tu mente pueda volar.',
      heroRecording: 'Grabando',
      visionImageAlt: 'Grabadora ScribAI junto a la aplicación',
      introImageAlt: 'Grabadora ScribAI, vista de detalle',
      loveItBannerAlt: 'Por qué te va a encantar ScribAI — transcripción con IA, 30 horas de batería, diseño ultrafino',
    },
    smartSummaries: {
      headline1: 'Resúmenes Inteligentes',
      headline2: 'y Mapas Mentales',
      subhead: 'Cada grabación se organiza automáticamente en resúmenes claros y estructurados que realmente puedes usar.',
      phoneHeader: 'Todas las Grabaciones',
      summarized: 'Resumido',
      useCase1: 'Grabación de Llamadas',
      useCase2: 'Actas de Reunión',
      useCase3: 'Transcripción de Clases',
      useCase4: 'Resumen de Entrevistas',
      useCase5: 'Mapas Mentales',
      useCase6: 'Tareas Pendientes',
    },
    // Experience page
    experience: {
      title: 'Inteligencia desde la voz.',
      subtitle: 'ScribAI va más allá de grabar. Entiende, organiza y entrega información accionable.',
      howItWorks: 'Cómo funciona',
      workflowSubtitle: 'Tres simples pasos para transformar tu productividad.',
      step1Title: 'Grabar',
      step1Desc: 'Presiona una vez para empezar. ScribAI captura hasta 30 horas de audio cristalino.',
      step2Title: 'Sincronizar',
      step2Desc: 'Conecta a la app. Tus grabaciones se cargan y procesan instantáneamente por IA.',
      step3Title: 'Acceder',
      step3Desc: 'Obtén tus transcripciones, resúmenes y tareas. Todo organizado y buscable.',
      featuresTitle: 'Funciones potentes.',
      featuresSubtitle: 'Todo lo que necesitas para capturar, entender y actuar en tus conversaciones.',
      feature1: 'Transcripción IA',
      feature1Desc: 'Voz a texto cristalino con identificación de hablantes. Soporta 104 idiomas con >98% de precisión.',
      feature2: 'Resúmenes Inteligentes',
      feature2Desc: 'Condensación automática de horas en puntos clave usando GPT-4.0.',
      feature3: 'Extracción de Ideas',
      feature3Desc: 'La IA identifica conceptos importantes, decisiones y tareas.',
      feature4: 'Generación de Tareas',
      feature4Desc: 'Crea automáticamente tareas de compromisos hablados.',
      feature5: 'Búsqueda Inteligente',
      feature5Desc: 'Encuentra cualquier momento en todas tus grabaciones al instante.',
      feature6: 'Auto Organización',
      feature6Desc: 'Notas categorizadas y etiquetadas por tema automáticamente.',
      useCasesTitle: 'Para todos.',
      useCasesSubtitle: 'ScribAI se adapta a tu mundo.',
      forStudents: 'Para Estudiantes',
      studentsDesc: 'Graba clases. Obtén apuntes. Aprueba tus exámenes.',
      forProfessionals: 'Para Profesionales',
      professionalsDesc: 'Domina cada reunión. Nunca pierdas una tarea.',
      forCreators: 'Para Creadores',
      creatorsDesc: 'Captura ideas. Organiza pensamientos. Crea más.',
      specsTitle: 'Diseñado para la vida real.',
      recording: 'Grabación',
      weight: 'Peso',
      audioQuality: 'Calidad de Audio',
      thin: 'Delgado',
      ctaTitle: '¿Listo para transformar cómo capturas ideas?',
      ctaSubtitle: 'Elige tu color y comienza.',
    },
    // Product page
    product: {
      chooseColor: 'Elige tu color.',
      from: 'Desde',
      color: 'Color',
      buyButton: 'Comprar',
      inTheBox: 'Qué incluye.',
      recorder: 'Grabadora ScribAI',
      cable: 'Cable de carga USB-C',
      case: 'Estuche Premium',
      guide: 'Guía de inicio rápido',
      subscription: '1 Año de ScribAI Premium',
      warranty: 'Tarjeta de garantía',
      specsTitle: 'Especificaciones.',
      model: 'Modelo',
      audioFormat: 'Formato de Audio',
      sampling: 'Muestreo',
      storage: 'Almacenamiento',
      battery: 'Batería',
      weightSpec: 'Peso',
      dimensions: 'Dimensiones',
      freeShipping: 'Envío Gratis',
      freeShippingDesc: 'Entrega mundial sin costo adicional.',
      returns: 'Devoluciones 30 días',
      returnsDesc: '¿No satisfecho? Reembolso completo, sin preguntas.',
      warrantyInfo: 'Garantía 2 Años',
      warrantyDesc: 'Te tenemos cubierto.',
      finalCta: 'ScribAI.',
    },
    // Footer
    footer: {
      brand: 'Tivorao',
      tagline: 'Redefiniendo cómo capturas la vida.',
      product: 'Producto',
      aboutTivorao: 'Sobre Tivorao',
      scribaiExperience: 'Experiencia ScribAI',
      getScribai: 'Obtener ScribAI',
      legal: 'Legal',
      privacy: 'Política de Privacidad',
      terms: 'Términos de Servicio',
      shipping: 'Envío',
      returns: 'Devoluciones y Reembolsos',
      contact: 'Contacto',
      legalNotice: 'Aviso Legal',
      securePayments: 'Pagos Seguros',
      copyright: '© 2026 Tivorao. Todos los derechos reservados.',
      newsletterTitle: 'Mantente al día',
      newsletterDesc: 'Novedades del producto y acceso anticipado. Sin spam.',
      newsletterPlaceholder: 'Tu email',
      newsletterButton: 'Suscribirme',
      newsletterSuccess: 'Ya estás en la lista.',
      newsletterInvalid: 'Introduce un email válido.',
    },
    // Legal pages
    legal: {
      privacyTitle: 'Política de Privacidad',
      termsTitle: 'Términos de Servicio',
      shippingTitle: 'Política de Envío',
      returnsTitle: 'Política de Devoluciones y Reembolsos',
      contactTitle: 'Información de Contacto',
      legalNoticeTitle: 'Aviso Legal',
      lastUpdated: 'Última actualización',
    },
  },
};

const detectBrowserLanguage = () => {
  if (typeof navigator === 'undefined') return 'es';
  const browserLang = navigator.language || navigator.userLanguage || '';
  return browserLang.toLowerCase().startsWith('en') ? 'en' : 'es';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('language');
      if (saved) return saved; // Respeta si el usuario ya eligió antes
      return detectBrowserLanguage(); // Si es su primera visita, detecta el idioma
    }
    return 'es';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
